import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { commonMessages } from 'src/utils/messages';
import useFilters from 'src/registry/hooks/useFilters';
import { chargesMessages } from '../../utils/messages';
import useSorting from 'src/registry/hooks/useSorting';
import usePagination from 'src/registry/hooks/usePagination';
import { useListCharges } from 'src/features/charges/api/useListCharges';
import { Charge } from 'src/features/charges/utils/types';
import {
    CHARGE_CATEGORY_BADGE_OPTIONS,
    CHARGE_STATUS_BADGE_OPTIONS,
    CHARGE_TYPE_BADGE_OPTIONS,
} from 'src/features/charges/utils/constants';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { useParams } from 'react-router-dom';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import React from 'react';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { Amount } from 'src/registry/components/Amount';

const ChargesTable = () => {
    const { formatMessage } = useIntl();
    const { customerId, subscriptionId, voucherId, discountId } = useParams();
    const [dateNow] = React.useState(() => Date.now());

    const permissionAccessSubscriptions = useHasPermissions([PERMISSION.ACCESS_SUBSCRIPTIONS]);
    const permissionAccessServicePlans = useHasPermissions([PERMISSION.ACCESS_SERVICE_PLANS]);
    const permissionAccessFinancialDocs = useHasPermissions([PERMISSION.ACCESS_FINANCIAL_DOCUMENTS]);

    const { querySearchOptions, ...search } = useSearch();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'billing_period_start', desc: true }],
    });

    const {
        data: chargesData,
        isLoading,
        isError,
    } = useListCharges({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...(customerId && { customer: customerId }),
        ...(subscriptionId && { subscription: subscriptionId }),
        ...(voucherId && { voucher: voucherId }),
        ...(discountId && { discount: discountId }),
    });

    const columnHelper = createColumnHelper<Charge>();

    const columns = [
        columnHelper.accessor('name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return <div className="whitespace-nowrap">{row.original.name}</div>;
            },
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('subscription_item.subscription.reference', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/subscriptions/${ctx.row.original.subscription_item.subscription.id}`}
                        hasPermissions={permissionAccessSubscriptions}
                    />
                );
            },
            meta: {
                title: formatMessage(chargesMessages.chargeCategorySub),
            },
        }),
        columnHelper.accessor('subscription_item.service_plan_item.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellLinkWithIcon
                    {...ctx}
                    to={`/subscriptions/${ctx.row.original.subscription_item.subscription.id}/subscription-item/${ctx.row.original.subscription_item.id}`}
                    hasPermissions={permissionAccessServicePlans}
                />
            ),
            meta: {
                title: formatMessage(chargesMessages.item),
            },
        }),
        columnHelper.accessor('type', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: CHARGE_TYPE_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('category', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(chargesMessages.category),
                badgeOptions: CHARGE_CATEGORY_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('external_product_id', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.external_product_id ?? '-';
            },
            enableColumnFilter: true,
            meta: {
                title: formatMessage(chargesMessages.externalProduct),
                inputType: INPUT_TYPE.TEXT,
            },
        }),
        columnHelper.accessor('external_source_id', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.external_source_id ?? '-';
            },
            enableColumnFilter: true,
            meta: {
                title: formatMessage(chargesMessages.externalSource),
                inputType: INPUT_TYPE.TEXT,
            },
        }),
        columnHelper.accessor('unit_amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            // <Amount> renders '-' for null itself, so the old guard would just be duplication.
            cell: ({ row }) => <Amount value={row.original.unit_amount} currency={row.original.currency} />,
            meta: {
                title: formatMessage(chargesMessages.unitAmount),
            },
        }),
        columnHelper.accessor('quantity', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => row.original.quantity ?? '-',
            meta: {
                title: formatMessage(chargesMessages.quantity),
            },
        }),
        columnHelper.accessor('amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => <Amount value={row.original.amount} currency={row.original.currency} />,
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('tax_amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => <Amount value={row.original.tax_amount} currency={row.original.currency} />,
            meta: {
                title: formatMessage(paymentsMessages.taxAmount),
            },
        }),
        columnHelper.accessor('amount_with_tax', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => <Amount value={row.original.amount_with_tax} currency={row.original.currency} />,
            meta: {
                title: formatMessage(paymentsMessages.amountWithTax),
            },
        }),
        columnHelper.accessor('status', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(chargesMessages.status),
                badgeOptions: CHARGE_STATUS_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('financial_document.number', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => (
                <CellLinkWithIcon
                    {...ctx}
                    to={`/financial-documents/${ctx.row.original.financial_document?.id}`}
                    hasPermissions={permissionAccessFinancialDocs}
                />
            ),
            meta: {
                title: formatMessage(financialDocumentsMessages.financialDocument),
            },
        }),
        columnHelper.accessor('billing_period_start', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(chargesMessages.periodStart),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('billing_period_end', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(chargesMessages.periodEnd),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        /*
         * Built server-side and rendered verbatim - never parsed, and never rebuilt from the raw
         * snapshot fields. Null means there is no derivation to show (credit charges, and charges
         * created before the billing engine started recording one).
         */
        columnHelper.accessor('price_explanation', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => row.original.price_explanation || '-',
            meta: {
                title: formatMessage(chargesMessages.priceExplanation),
                isLongText: true,
            },
            minSize: 220,
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={chargesData}
            semantics={{
                title: formatMessage(chargesMessages.charges),
                entryName: formatMessage(chargesMessages.charge).toLowerCase(),
                entryNames: formatMessage(chargesMessages.charges).toLowerCase(),
                placeholder: formatMessage(chargesMessages.charge).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default ChargesTable;
